'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    clienteApi,
    haySesionProbable,
    olvidarTokenDeSesion,
    barrerCredencialLegada,
} from '@/services/clienteApi';

// Define User Interface based on usage in the app
export interface User {
    id: string;
    email: string;
    nombre?: string;
    apellidos?: string;
    role?: string;
    permisosAdmin?: string[];
    estadoBeta?: string;
    fechaInicioBeta?: string;
    fechaFinBeta?: string;
    fechaUltimaTransicionBeta?: string;
    motivoEstadoBeta?: string;
    descuentoBetaPorcentaje?: number;
    descuentoBetaPermanente?: boolean;
    rolOrganizacion?: 'propietario' | 'miembro' | 'freelance' | string;
    photoUrl?: string;
    [key: string]: any; // Allow extensibility for now
}

interface AuthContextType {
    user: User | null;
    setUser: (user: User | null) => void;
    login: (userData: User, mfaRequired?: boolean) => void;
    logout: () => Promise<void>;
    refreshUser: () => Promise<boolean>;
    loading: boolean;
    /**
     * Sólo `true` cuando `/api/auth/verify` ya contestó y se sabe si la
     * sesión es válida. `loading` sigue teniendo el mismo timing de siempre
     * -- esta señal es la que los guardas de redirección deben esperar,
     * porque `user` puede seguir siendo el objeto cacheado aunque
     * `loading` ya sea `false` (D-25).
     */
    sesionConfirmada: boolean;
    /**
     * Única señal de MFA que viaja al cliente: hay un segundo factor pendiente
     * para una cuenta que ya lo tiene activado. No existe ninguna señal de
     * "falta configurarlo" -- configurarlo es voluntario y se hace desde
     * ajustes, así que no es una condición que pueda bloquear a nadie.
     */
    mfaRequired?: boolean;
}

// 1. Crear el Contexto
const AuthContext = createContext<AuthContextType>({
    user: null,
    setUser: () => { },
    login: () => { },
    logout: async () => { },
    refreshUser: async () => false,
    loading: true,
    sesionConfirmada: false,
    mfaRequired: false,
});

// 2. Crear el "Proveedor"
export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUserState] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [sesionConfirmada, setSesionConfirmada] = useState(false);
    const [mfaRequired, setMfaRequired] = useState(false);

    // 3. Efecto para cargar y verificar el usuario
    useEffect(() => {
        const loadUserFromToken = async () => {
            // La cookie legible de la época del modo bearer puede tapar a la
            // httpOnly cuando conviven: el backend se queda con la primera de
            // las dos que manda el navegador. Se barre antes de preguntar nada.
            barrerCredencialLegada();

            const cachedUser = localStorage.getItem('authUser');

            // PASO 1: Hidratar inmediatamente desde cache si existe
            // Esto DEBE ocurrir ANTES de cualquier verificación async
            let usuarioLocal: User | null = null;
            if (cachedUser) {
                try {
                    usuarioLocal = JSON.parse(cachedUser);
                    setUserState(usuarioLocal);
                } catch {
                    localStorage.removeItem('authUser');
                }
            }

            // PASO 2: Si no hay sesión, mantener usuario del cache (sesión offline)
            // Solo limpiar si no hay NADA (ni sesión ni cache)
            if (!haySesionProbable()) {
                if (!usuarioLocal) {
                    setUserState(null);
                }
                setLoading(false);
                return;
            }

            // PASO 3: Verificación en background
            // El usuario ya está visible desde el cache
            setLoading(false);

            try {
                // `toleraNoAutorizado` porque el 401 lo trata este archivo: el
                // manejo por defecto de clienteApi redirige al login, y acá un
                // token muerto debe limpiar la sesión sin arrancar al usuario de
                // la pantalla en la que esté.
                const res = await clienteApi.crudo('/api/auth/verify', { toleraNoAutorizado: true });

                if (res.ok) {
                    const { user: fetchedUser, mfaRequired: mfaReq } = await res.json();
                    localStorage.setItem('authUser', JSON.stringify(fetchedUser));
                    setUserState(fetchedUser);
                    setMfaRequired(!!mfaReq);
                    // El estado ya se sabe: los guardas pueden decidir (D-25).
                    setSesionConfirmada(true);
                } else if (res.status === 401 || res.status === 403) {
                    console.error('[AuthContext] Sesión inválida (401/403). Cerrando sesión.');
                    olvidarTokenDeSesion();
                    localStorage.removeItem('authUser');
                    setUserState(null);
                    // También se sabe: no hay sesión, y los guardas pueden
                    // decidir mandar al login (D-25/D-27).
                    setSesionConfirmada(true);
                } else {
                    console.warn(`[AuthContext] Error de servidor (${res.status}). Manteniendo sesión local.`);
                    // 5xx: el estado sigue sin conocerse -- no se toca
                    // `sesionConfirmada` para no autorizar redirecciones
                    // sobre una respuesta que no dijo nada (D-25/D-34).
                }
            } catch (e) {
                console.warn('[AuthContext] Error de red verificando sesión. Manteniendo sesión local.', e);
                // Igual que el 5xx: sin respuesta del servidor no hay nada
                // confirmado, así que ningún guarda debe redirigir (D-34).
            }
        };

        loadUserFromToken();
    }, []);

    // 4. Función 'login'
    // Sin parámetro `token`, y ese es el final del camino que empezó con la
    // cookie httpOnly. El backend ya no lo devuelve en el JSON, así que el
    // argumento que once llamadas seguían pasando valía `undefined` y se lo
    // tragaba una función que en modo cookie no hacía nada. Un parámetro que
    // nadie usa y que nadie puede usar es una invitación a que el próximo lector
    // crea que el token sigue importando.
    const login = (userData: User, mfaReq = false) => {
        localStorage.setItem('authUser', JSON.stringify(userData));
        setUserState(userData);
        setMfaRequired(mfaReq);
    };

    const setUser = (userData: User | null) => {
        // Por compatibilidad
        if (userData) {
            localStorage.setItem('authUser', JSON.stringify(userData));
        } else {
            localStorage.removeItem('authUser');
        }
        setUserState(userData);
    }

    const logout = async () => {
        // Avisar al servidor es lo que de verdad cierra la sesión, y hasta ahora
        // no se hacía: el frontend se limitaba a tirar su copia del token.
        //
        // Con la cookie httpOnly eso deja de alcanzar, porque JavaScript no
        // puede borrarla: sin esta llamada el usuario acabaría en el login con
        // la sesión intacta en el navegador y viva en el servidor. Sólo
        // `POST /logout` borra la cookie y revoca la fila en `sesiones`.
        //
        // El fallo no interrumpe la salida. Alguien que pulsa "cerrar sesión"
        // sin red tiene que quedar fuera de esta máquina igualmente; la sesión
        // del servidor seguirá viva y se cerrará al caducar o desde el listado
        // de dispositivos.
        try {
            await clienteApi.crear('/api/auth/logout', undefined, { toleraNoAutorizado: true });
        } catch (error) {
            console.warn('[AuthContext] No se pudo avisar al servidor del cierre de sesión.', error);
        }

        localStorage.removeItem('authUser');
        olvidarTokenDeSesion();
        setUserState(null);
        setMfaRequired(false);
        if (typeof window !== 'undefined') {
            window.location.href = '/app/login';
        }
    };

    // T-711.2: Función para forzar actualización de sesión desde el servidor
    const refreshUser = async (): Promise<boolean> => {

        if (!haySesionProbable()) {
            return false;
        }

        try {
            const res = await clienteApi.crudo('/api/auth/verify', { toleraNoAutorizado: true });

            if (res.ok) {
                const { user: fetchedUser, mfaRequired: mfaReq } = await res.json();

                localStorage.setItem('authUser', JSON.stringify(fetchedUser));

                setUserState(fetchedUser);
                setMfaRequired(!!mfaReq);
                // Un refresh confirmado no debe volver a dejar el contexto
                // en estado "sin confirmar" (D-25).
                setSesionConfirmada(true);

                return true;
            } else if (res.status === 401 || res.status === 403) {
                console.error('Sesión inválida o expirada (401/403) en refreshUser.');
                olvidarTokenDeSesion();
                localStorage.removeItem('authUser');
                setUserState(null);
                setSesionConfirmada(true);
                if (typeof window !== 'undefined') {
                    window.location.href = '/app/login';
                }
                return false;
            } else {
                console.warn(`[AuthContext] Error refreshUser: ${res.status}.`);
                return false;
            }
        } catch {
            return false;
        }
    };

    return (
        <AuthContext.Provider value={{
            user, setUser, login, logout, refreshUser, loading, sesionConfirmada,
            mfaRequired
        }}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Ya NO devuelve el token, y ese es el punto.
 *
 * Repartirlo desde acá convertía a cada consumidor en un sitio más que compone
 * su propia credencial, y sobre todo era una trampa esperando a la cookie
 * httpOnly: `Cookies.get` pasa a devolver `undefined` y los guardas del estilo
 * `if (!token) return;` rechazan en silencio a usuarios perfectamente
 * logueados. Sin excepción, sin error, sin nada en la consola.
 *
 * Quien necesitaba el token para llamar a la API usa `clienteApi`, que compone
 * la credencial en un único sitio. Quien lo usaba solo para preguntar "¿hay
 * sesión?" usa `haySesionProbable()`, que responde bien en los dos modos.
 */
export const useAuth = () => useContext(AuthContext);
